import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { QuestionModel } from 'src/app/api/models';
import { QuestionsService, QuizzesService } from 'src/app/api/services';
import {
  CommentListDialogComponent,
  CommentListDialogData,
} from 'src/app/cms/list/comment-list-dialog.component';
import { PanelService } from '../panel.service';
import { Panel, PanelEntityType } from '../panel.types';
import { TranslateService } from '@ngx-translate/core';
import { QuestionType, questionTypeDisplayFn } from 'src/app/app.types';
import { ImportEntity, WizardImports } from '../../list/list.types';
import {
  ImportAiQuestionComponent,
  ImportQuestionType,
} from '../../common/dialogs/import-ai-question/import-ai-question.component';
import { validateQuestions } from 'src/app/utility/app.utils';
import { MatSnackBar } from '@angular/material/snack-bar';
import { of } from 'rxjs';
import { AmplitudeSource } from 'src/app/utility/amplitude-tracking/tracking.types';

/* TODO: 
  It used in books module which has old import options design.
  so this imports will be removed in future.
*/
export const getQuestionImports = (
  translator: TranslateService,
  panelService: PanelService,
  panel: Panel<QuestionModel>,
  matDialog: MatDialog,
  courseContext = false,
  allowAiForSingleQuestionGeneration = false
): ImportEntity<QuestionModel>[] =>
  (
    [
      allowAiForSingleQuestionGeneration
        ? {
            id: 'chat-gpt',
            title: translator.instant(
              'content.question.description.aiImportTitle'
            ),
            description: translator.instant(
              'content.question.description.aiImportDescription'
            ),
            highlight: true,
            action: {
              icon: 'code',
              click: () => {
                matDialog
                  .open<ImportAiQuestionComponent, ImportQuestionType>(
                    ImportAiQuestionComponent,
                    {
                      data: {
                        type: courseContext ? 'quiz' : null,
                      },
                      autoFocus: false,
                      width: '600px',
                      maxHeight: '90vh',
                    }
                  )
                  .afterClosed()
                  .subscribe((r) => {
                    if (r) {
                      matDialog.closeAll();
                      panelService.fetchItems(panel);
                    }
                  });
              },
              text: translator.instant(
                'content.question.addMenu.generateQuestions'
              ),
            },
          }
        : null,
      {
        title: translator.instant(
          'content.examQuestion.title.generateQuestionsWithChatGPT'
        ),
        description: translator.instant(
          'content.question.description.generateQuestionWithChatGPT'
        ),
        action: {
          icon: 'code',
          click: () => {
            if (courseContext && panel) {
              panelService.importFromChatGPT(panel);
            } else {
              const callback = () => {
                const queryParams = {
                  type: PanelEntityType.Question,
                };
                panelService.redirectToImportItems(
                  `/cms/csv-imports/question`,
                  queryParams,
                  true
                );
              };
              panelService.importFromChatGPT(null, () => callback());
            }
          },
          text: translator.instant(
            'content.question.addMenu.generateQuestions'
          ),
        },
      },
      courseContext
        ? {
            title: translator.instant(
              'content.examQuestion.title.addExistingQuestions'
            ),
            description: translator.instant(
              'content.examQuestion.description.addExistingQuestions'
            ),
            action: {
              icon: 'move_to_inbox',
              click: () => panelService.importItems(panel),
              text: translator.instant(
                'content.question.addMenu.existingQuestions'
              ),
            },
          }
        : null,
      {
        id: 'import-as-csv',
        title: translator.instant(
          'content.examQuestion.title.importQuestionsFromTableExcelCSV'
        ),
        description: translator.instant(
          'content.examQuestion.description.importQuestionsFromTableExcelCSV'
        ),
        action: {
          icon: 'description',
          click: () => {
            panelService.importItemAsCSV(panel, AmplitudeSource.QUIZ);
          },
          text: translator.instant(
            'content.question.addMenu.questionsFromExcelCSV'
          ),
        },
        extra: {
          name: translator.instant('content.examQuestion.title.downloadCSV'),
          type: 'link',
          icon: 'download',
          click: () => {
            window.open(
              'assets/import-csv-templates/Sample-import-questions.csv',
              '_blank'
            );
          },
        },
      },
      {
        id: 'import-json',
        title: translator.instant(
          'content.examQuestion.title.importQuestionsFromJSON'
        ),
        description: translator.instant(
          'content.examQuestion.description.importQuestionsFromJSON'
        ),
        action: {
          icon: 'code',
          click: () => panelService.importItemAsJSON(panel),
          text: translator.instant('content.question.addMenu.importFromJSON'),
        },
      },
    ] as ImportEntity<QuestionModel>[]
  ).filter((n) => n !== null);

export const getWizardQuestionsImports = (
  translator: TranslateService,
  panelService: PanelService,
  panel: Panel<QuestionModel>,
  matDialog: MatDialog,
  courseContext = false,
  allowAiForSingleQuestionGeneration = false
): WizardImports => {
  return {
    createWithAI: {
      text: translator.instant('content.wizardActions.title.createWithAI'),
      icon: 'auto_awesome',
      description: translator.instant(
        'content.wizardActions.description.createQuestionWithAI'
      ),
      tag: translator.instant('content.wizardActions.tag.createWithAI'),
      click: () => {
        if (courseContext && panel) {
          panelService.importFromChatGPT(panel);
        } else {
          const callback = () => {
            const queryParams = {
              type: PanelEntityType.Question,
            };
            panelService.redirectToImportItems(
              `/cms/csv-imports/question`,
              queryParams,
              true
            );
          };
          panelService.importFromChatGPT(null, () => callback());
        }
      },
    },
    createButton: {
      text: translator.instant('content.wizardActions.title.createManually'),
      icon: 'edit',
      description: translator.instant(
        'content.wizardActions.description.createManually'
      ),
      click: () => {
        if (panel) {
          panelService.editItem(panel);
          matDialog.closeAll();
        } else {
          panelService.redirectToImportItems('/cms/add/question', null, true);
        }
      },
    },
    importButtons: [
      {
        text: translator.instant(
          'content.wizardActions.title.createIndividualQuestionsWithAI'
        ),
        description: translator.instant(
          'content.wizardActions.description.createIndividualQuestionsWithAI'
        ),
        icon: 'auto_awesome',
        hidden: !allowAiForSingleQuestionGeneration,
        click: () => {
          matDialog
            .open<ImportAiQuestionComponent, ImportQuestionType>(
              ImportAiQuestionComponent,
              {
                data: {
                  type: courseContext ? 'quiz' : null,
                },
                autoFocus: false,
                width: '600px',
                maxHeight: '90vh',
              }
            )
            .afterClosed()
            .subscribe((r) => {
              if (r) {
                matDialog.closeAll();
                panelService.fetchItems(panel);
              }
            });
        },
      },
      {
        text: translator.instant(
          'content.wizardActions.title.useExistingQuestions'
        ),
        description: translator.instant(
          'content.wizardActions.description.useExistingQuestions'
        ),
        icon: 'file_copy',
        hidden: !courseContext,
        click: () => panelService.importItems(panel),
      },
      {
        id: 'import-json',
        text: translator.instant('content.wizardActions.title.importFromJSON'),
        description: translator.instant(
          'content.wizardActions.description.importFromJSON'
        ),
        icon: 'save_alt',
        click: () => panelService.importItemAsJSON(panel),
      },
      {
        id: 'import-csv',
        text: translator.instant('content.wizardActions.title.importFromCSV'),
        description: translator.instant(
          'content.wizardActions.description.importFromCSV'
        ),
        icon: 'save_alt',
        click: () => {
          panelService.importItemAsCSV(panel, AmplitudeSource.QUIZ);
        },
      },
    ],
  };
};
@Injectable()
export class QuestionPanel extends Panel<QuestionModel> {
  constructor(
    matDialog: MatDialog,
    panelService: PanelService,
    questions: QuestionsService,
    quizzes: QuizzesService,
    translator: TranslateService,
    matSnackBar: MatSnackBar
  ) {
    super();
    super.init({
      addMenuItems: [
        {
          icon: 'add',
          onClick: () => panelService.editItem(this),
          text: translator.instant(
            'content.question.addMenu.createNewQuizQuestion'
          ),
        },
      ],
      showChatGptAction: true,
      wizardImports: getWizardQuestionsImports(
        translator,
        panelService,
        this,
        matDialog,
        true,
        true
      ),
      imports: getQuestionImports(
        translator,
        panelService,
        this,
        matDialog,
        true,
        true
      ),
      headerActions: [],
      changeItemPosition: (questionId, quizId, position) => {
        return quizzes.repositionQuestionInQuiz({
          id: quizId,
          question_id: questionId,
          body: { position },
        });
      },
      showTotalItems: true,
      createItemsFn: (items, parentId) => {
        const hasFreeTextQuestion = items.some(
          (q) => q.type === QuestionType.FREETEXT
        );
        if (hasFreeTextQuestion) {
          return of({
            error: translator.instant('quiz.message.freeTextNotAllowed'),
          });
        }
        const sentryInfo = {
          title: `Error while importing questions`,
          file: 'question-panel.ts - createItemsFn',
          api: 'copyQuestionToQuiz',
          preventLogs: true,
        };
        const { isValid, error } = validateQuestions(
          sentryInfo,
          items,
          translator,
          matSnackBar
        );
        if (!isValid) {
          return of({ error });
        }
        return quizzes.copyQuestionToQuiz({
          id: parentId,
          body: items,
        });
      },
      deleteItem: (questionId) => questions.deleteQuestion({ id: questionId }),
      editDialogInput: (question, quizId) => ({
        enableNavigation: true,
        question,
        quizId,
      }),
      fetchItem: (id) => questions.getQuestionById({ id }),
      fetchItems: (quizId, additionalData) =>
        additionalData
          ? quizzes.getQuestionsForQuiz({
              id: quizId,
              embed_answers: true,
              embed_tags: true,
            })
          : quizzes.getQuestionsForQuiz({
              id: quizId,
            }),
      headerIcon: 'question_answers',
      itemActions: [
        {
          icon: 'question_answer',
          label: translator.instant('content.question.actionMenu.comments'),
          onClick: (question) => {
            matDialog.open(CommentListDialogComponent, {
              data: {
                questionId: question.id,
              },
              autoFocus: false,
            } as MatDialogConfig<CommentListDialogData>);
          },
        },
        {
          icon: 'code',
          label: translator.instant('content.question.actionMenu.downloadJSON'),
          onClick: (item) => panelService.exportItemAsJSON(this, item.id),
        },
        {
          icon: 'link_off',
          label: translator.instant(
            'content.question.actionMenu.removeFromQuiz'
          ),
          disabled: (question) => question['isImported'],
          tooltip: (question) =>
            question['isImported']
              ? translator.instant('content.quiz.notYourQuetion')
              : null,
          onClick: (question) => panelService.unlinkItems(this, question.id),
        },
        {
          icon: 'delete',
          label: translator.instant('content.question.actionMenu.delete'),
          disabled: (question) => question['isImported'],
          tooltip: (question) =>
            question['isImported']
              ? translator.instant('content.quiz.notYourQuetion')
              : null,
          onClick: (question) => panelService.deleteItem(this, question.id),
        },
      ],
      itemPrefixes: (question) => {
        const type = questionTypeDisplayFn(question.type, true) ?? '?';
        const suffixes = [{ text: type, icon: null }];
        return suffixes;
      },
      itemTextProperty: 'text',
      itemHeaderText: 'Text',
      parentKey: 'questions',
      parentType: PanelEntityType.Quiz,
      selectedItemActions: [
        {
          icon: 'link_off',
          label: translator.instant('content.question.selectedItem.label'),
          onClick: (selectedItemIds) =>
            panelService.unlinkItems(this, selectedItemIds),
        },
      ],
      translations: {
        addItem: translator.instant('content.question.tooltip'),
        deleteItemError: translator.instant(
          'content.question.deleteItem.error'
        ),
        deleteItemSuccess: translator.instant(
          'content.question.deleteItem.success'
        ),
        deleteItemText: translator.instant('content.question.deleteItem.text'),
        deleteItemTitle: translator.instant(
          'content.question.deleteItem.title'
        ),
        header: translator.instant('common.label.questions'),
        unlinkItemConfirmText: translator.instant(
          'content.question.unlinkItem.text'
        ),
        unlinkItemConfirmTitle: translator.instant(
          'content.question.unlinkItem.title'
        ),
        unlinkItemError: translator.instant(
          'content.question.unlinkItem.error'
        ),
        unlinkItemSuccess: translator.instant(
          'content.question.unlinkItem.success'
        ),
      },
      type: PanelEntityType.Question,
      unlinkItems: (questionIds, quizId) =>
        quizzes.removeQuestionFromQuiz({
          id: quizId,
          question_ids: questionIds.join(','),
        }),
    });
  }
}
